/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { ArrowLeft, Eye, EyeOff } from 'lucide-react'

import { useAuthStore } from '@/store/auth.store'
import { ROUTES } from '@/constants'
import { cn } from '@/utils/cn'
import { AuthLogo } from '@/components/auth-logo'
import { Button, FormInput } from '@/components'
import { UserRole } from '@/types'

const STEPS = [
  { number: 1, label: 'Your info' },
  { number: 2, label: 'Add child' },
  { number: 3, label: 'Book a demo class' },
] as const

const step1Schema = z
  .object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: z
      .string()
      .min(1, 'Email is required')
      .email('Please enter a valid email'),
    location: z.string().min(1, 'Location is required'),
    relationship: z.string().min(1, 'Relationship is required'),
    phoneNumber: z.string().min(1, 'Phone number is required'),
    password: z.string().min(8, 'Must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

const childSchema = z.object({
  childName: z.string().min(1, "Child's name is required"),
  age: z.string().min(1, 'Age is required'),
  grade: z.string().min(1, 'Grade is required'),
})

const step2Schema = z.object({
  children: z.array(childSchema).min(1, 'Add at least one child'),
})

const step3Schema = z.object({
  preferredDate: z.string().min(1, 'Please select a preferred date'),
  preferredTime: z.string().min(1, 'Please select a preferred time'),
})

type Step1Values = z.infer<typeof step1Schema>
type Step2Values = z.infer<typeof step2Schema>
type Step3Values = z.infer<typeof step3Schema>

const RELATIONSHIP_OPTIONS = ['Mother', 'Father', 'Guardian', 'Other']

const DEMO_DATE_OPTIONS = [
  { id: 'tomorrow', label: 'Tomorrow', day: '14' },
  { id: 'thu', label: 'Thursday', day: '15' },
  { id: 'fri', label: 'Friday', day: '16' },
  { id: 'sat', label: 'Saturday', day: '17' },
  { id: 'sun', label: 'Sunday', day: '18' },
  { id: 'mon', label: 'Monday', day: '19' },
]

const DEMO_TIME_SLOTS = [
  '8:30 AM',
  '9:30 AM',
  '10:30 AM',
  '11:30 AM',
  '12:30 PM',
  '1:30 PM',
  '2:30 PM',
  '3:30 PM',
  '4:30 PM',
  '5:30 PM',
  '6:30 PM',
  '7:30 PM',
  '8:30 PM',
  '9:30 PM',
  '10:30 PM',
]

export const Signup: React.FC = () => {
  const [step, setStep] = useState(1)
  const [serverError, setServerError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const { register: registerUser, isLoading } = useAuthStore()
  const navigate = useNavigate()

  const step1Form = useForm<Step1Values>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      location: '',
      relationship: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
    },
    mode: 'onTouched',
  })

  const step2Form = useForm<Step2Values>({
    defaultValues: {
      children: [{ childName: '', age: '', grade: '' }],
    },
    mode: 'onTouched',
  })

  const step3Form = useForm<Step3Values>({
    defaultValues: {
      preferredDate: '',
      preferredTime: '',
    },
    mode: 'onTouched',
  })

  const step1Errors = step1Form.formState.errors
  const step2Errors = step2Form.formState.errors
  const step3Errors = step3Form.formState.errors

  const onStep1Submit = step1Form.handleSubmit(data => {
    const result = step1Schema.safeParse(data)
    if (!result.success) {
      result.error.errors.forEach(err => {
        const path = err.path[0] as keyof Step1Values
        step1Form.setError(path, { message: err.message })
      })
      return
    }
    setServerError('')
    setStep(2)
  })

  const onStep2Submit = step2Form.handleSubmit(data => {
    const result = step2Schema.safeParse(data)
    if (!result.success) {
      step2Form.clearErrors('root')
      result.error.errors.forEach(err => {
        if (err.path[0] === 'children' && typeof err.path[1] === 'number') {
          const field = err.path[2] as string
          step2Form.setError(`children.${err.path[1]}.${field}` as any, {
            message: err.message,
          })
        } else {
          step2Form.setError('root', {
            message: 'Add at least one child with a name.',
          })
        }
      })
      return
    }
    setServerError('')
    setStep(3)
  })

  const onStep3Submit = step3Form.handleSubmit(async () => {
    setServerError('')
    const step1Data = step1Form.getValues()
    const result = step1Schema.safeParse(step1Data)
    if (!result.success) {
      setStep(1)
      return
    }

    try {
      await registerUser({
        firstName: result.data.firstName,
        lastName: result.data.lastName,
        email: result.data.email,
        password: result.data.password,
        role: UserRole.PARENT,
      })
      navigate(ROUTES.PARENT.DASHBOARD, { replace: true })
    } catch (err) {
      setServerError(
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again.'
      )
    }
  })

  return (
    <div className="min-h-screen flex flex-col items-center bg-white py-8 px-4 md:mt-10">
      <Link
        to="/"
        className="flex items-center gap-2 text-[#222222] text-base font-normal mb-6 hover:text-gray-700"
      >
        <ArrowLeft className="w-4 h-4 shrink-0" />
        Back to Home
      </Link>
      <AuthLogo className="h-14 w-auto" alt="Code Ambassadors" />
      <p className="text-[#222222] text-base font-normal mb-8 mt-4">
        Sign in to continue your coding journey
      </p>

      <div className="w-full max-w-2xl flex items-center justify-center mb-8">
        {STEPS.map((s, index) => (
          <div
            key={s.number}
            className="flex items-center flex-1 last:flex-none"
          >
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold shrink-0',
                  step >= s.number
                    ? 'bg-[#34A853] text-white'
                    : 'bg-gray-200 text-gray-500'
                )}
              >
                {s.number}
              </div>
              <span
                className={cn(
                  'text-sm font-medium hidden sm:inline',
                  step >= s.number ? 'text-[#222222]' : 'text-gray-500'
                )}
              >
                {s.label}
              </span>
            </div>
            {index < STEPS.length - 1 && (
              <div
                className={cn(
                  'flex-1 h-0.5 mx-2 min-w-[16px]',
                  step > s.number ? 'bg-[#34A853]' : 'bg-gray-200'
                )}
              />
            )}
          </div>
        ))}
      </div>

      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8">
        {step === 1 && (
          <>
            <h1 className="text-xl sm:text-2xl font-semibold text-[#222222] mb-6">
              Create Your Account
            </h1>
            <form onSubmit={onStep1Submit} className="space-y-5">
              {serverError && (
                <div className="rounded-lg bg-red-50 border border-red-100 p-3">
                  <p className="text-sm text-red-700">{serverError}</p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <FormInput
                  {...step1Form.register('firstName')}
                  id="firstName"
                  label="First Name"
                  placeholder="e.g. Jane"
                  required
                  error={step1Errors.firstName?.message}
                />
                <FormInput
                  {...step1Form.register('lastName')}
                  id="lastName"
                  label="Last Name"
                  placeholder="e.g. Doe"
                  required
                  error={step1Errors.lastName?.message}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <FormInput
                  {...step1Form.register('email')}
                  id="email"
                  label="Email Address"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  required
                  error={step1Errors.email?.message}
                />
                <FormInput
                  {...step1Form.register('location')}
                  id="location"
                  label="Location"
                  placeholder="e.g. Houston, Texas"
                  required
                  error={step1Errors.location?.message}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Relationship
                  </label>
                  <select
                    {...step1Form.register('relationship')}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#34A853] focus:border-transparent"
                  >
                    <option value="">Select relationship</option>
                    {RELATIONSHIP_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                  {step1Errors.relationship && (
                    <p className="text-xs text-red-600 mt-1">
                      {step1Errors.relationship.message}
                    </p>
                  )}
                </div>
                <FormInput
                  {...step1Form.register('phoneNumber')}
                  id="phoneNumber"
                  label="Phone Number"
                  type="tel"
                  placeholder="e.g. +1 234 567 8900"
                  required
                  error={step1Errors.phoneNumber?.message}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="relative">
                  <FormInput
                    {...step1Form.register('password')}
                    id="password"
                    label="Choose Password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    required
                    helperText="Must be at least 8 characters"
                    error={step1Errors.password?.message}
                    inputClassName="pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(p => !p)}
                    className="absolute right-3 top-[38px] text-gray-500 hover:text-gray-700"
                    aria-label={
                      showPassword ? 'Hide password' : 'Show password'
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                <div className="relative">
                  <FormInput
                    {...step1Form.register('confirmPassword')}
                    id="confirmPassword"
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    required
                    error={step1Errors.confirmPassword?.message}
                    inputClassName="pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(p => !p)}
                    className="absolute right-3 top-[38px] text-gray-500 hover:text-gray-700"
                    aria-label={
                      showConfirmPassword ? 'Hide password' : 'Show password'
                    }
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                fullWidth
                className="bg-[#34A853] hover:bg-green-600 focus:ring-[#34A853] text-white mt-4"
              >
                Continue
              </Button>
            </form>
          </>
        )}

        {step === 2 && (
          <>
            <h1 className="text-xl sm:text-2xl font-semibold text-[#222222] mb-2">
              Add Your First child
            </h1>
            <p className="text-[#717392] text-sm mb-6">
              Add your first child now. You can add more children later.
            </p>
            <form onSubmit={onStep2Submit} className="space-y-5">
              {step2Errors.root && (
                <p className="text-sm text-red-600">
                  {step2Errors.root.message}
                </p>
              )}
              <div className="space-y-4">
                <FormInput
                  {...step2Form.register('children.0.childName')}
                  label="Child's Name"
                  placeholder="e.g. Crystal clear"
                  required
                  error={
                    (step2Errors.children?.[0] as any)?.childName?.message as
                      | string
                      | undefined
                  }
                />
                <FormInput
                  {...step2Form.register('children.0.age')}
                  label="Age"
                  placeholder="e.g. 10"
                  type="number"
                  min={1}
                  max={18}
                  required
                  error={
                    (step2Errors.children?.[0] as any)?.age?.message as
                      | string
                      | undefined
                  }
                />
                <FormInput
                  {...step2Form.register('children.0.grade')}
                  label="Grade"
                  placeholder="e.g. 5"
                  required
                  error={
                    (step2Errors.children?.[0] as any)?.grade?.message as
                      | string
                      | undefined
                  }
                />
              </div>
              <div className="mt-6 flex flex-col sm:flex-col gap-3">
                <Button
                  type="submit"
                  fullWidth
                  variant="primary"
                  className="bg-[#F28C28] hover:bg-portal-orange-hover focus:ring-portal-orange"
                >
                  Continue
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  fullWidth
                  onClick={() => setStep(1)}
                  className="bg-white text-gray-900 border-2 border-[#E5E5E5]"
                >
                  Back
                </Button>
              </div>
            </form>
          </>
        )}

        {step === 3 && (
          <>
            <form onSubmit={onStep3Submit} className="space-y-6">
              {serverError && (
                <div className="rounded-lg bg-red-50 border border-red-100 p-3">
                  <p className="text-sm text-red-700">{serverError}</p>
                </div>
              )}

              <div>
                <div className="mb-5">
                  <p className="text-base font-semibold text-gray-700">
                    Select Date
                  </p>
                  <p className="text-sm text-[#717392]">
                    Your class will be for 1 hour
                  </p>
                </div>
                <input type="hidden" {...step3Form.register('preferredDate')} />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {DEMO_DATE_OPTIONS.map(option => {
                    const selected =
                      step3Form.watch('preferredDate') === option.id
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() =>
                          step3Form.setValue('preferredDate', option.id, {
                            shouldValidate: true,
                            shouldDirty: true,
                          })
                        }
                        className={cn(
                          'flex flex-col items-center justify-center rounded-xl border px-3 py-2 text-xs sm:text-sm font-medium bg-white',
                          selected
                            ? 'border-2 border-[#34A853] text-[#222222]'
                            : 'border-gray-300 text-gray-700 hover:border-gray-400'
                        )}
                      >
                        <span>{option.label}</span>
                        <span className="text-base sm:text-lg font-semibold">
                          {option.day}
                        </span>
                      </button>
                    )
                  })}
                </div>
                {step3Errors.preferredDate && (
                  <p className="text-xs text-red-600 mt-2">
                    {step3Errors.preferredDate.message}
                  </p>
                )}
              </div>

              <div>
                <div className="mb-6">
                  <p className="text-base font-semibold text-gray-700">
                    Select Start Time
                  </p>
                  <p className="text-xs text-[#717392]">
                    Class slots are limited. Please choose carefully.
                  </p>
                </div>
                <input type="hidden" {...step3Form.register('preferredTime')} />
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {DEMO_TIME_SLOTS.map(slot => {
                    const selected = step3Form.watch('preferredTime') === slot
                    return (
                      <button
                        key={slot}
                        type="button"
                        onClick={() =>
                          step3Form.setValue('preferredTime', slot, {
                            shouldValidate: true,
                            shouldDirty: true,
                          })
                        }
                        className={cn(
                          'rounded-xl border px-3 py-2 text-xs sm:text-sm font-medium bg-white',
                          selected
                            ? 'border-2 border-[#34A853] text-[#222222]'
                            : 'border-gray-300 text-gray-700 hover:border-gray-400'
                        )}
                      >
                        {slot}
                      </button>
                    )
                  })}
                </div>
                {step3Errors.preferredTime && (
                  <p className="text-xs text-red-600 mt-2">
                    {step3Errors.preferredTime.message}
                  </p>
                )}
              </div>

              <div className="pt-10 flex flex-col gap-3">
                <Button
                  type="submit"
                  fullWidth
                  loading={isLoading}
                  loadingLabel="Booking demo..."
                  variant="primary"
                  className="bg-[#F28C28] hover:bg-portal-orange-hover focus:ring-portal-orange"
                >
                  Book a Free Trial Class
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  fullWidth
                  onClick={() => setStep(2)}
                  className="bg-white text-gray-900 border-2 border-[#E5E5E5]"
                >
                  Back
                </Button>
              </div>
            </form>
          </>
        )}

        <p className="text-center text-sm text-gray-600 mt-6 pt-4 border-t border-gray-100">
          Already have an account?{' '}
          <Link
            to={ROUTES.LOGIN}
            className="font-medium text-[#34A853] hover:underline"
          >
            Login
          </Link>
        </p>
      </div>
    </div>
  )
}
